<div class="ws-page plot-boundaries-page">
  <header class="page-head">
    <div>
      <p class="kicker">Administration · GIS</p>
      <div class="page-head-title-row">
        <app-page-back-btn />
        <h2>Plot boundaries</h2>
        <app-page-refresh-btn [loading]="loading()" (refresh)="reloadLocations()" />
      </div>
      <p class="muted ws-lead">Set each plot's shape on the map so LiDAR scans can match buildings correctly.</p>
    </div>
    <div class="actions">
      <a routerLink="/enforcement/areas" class="btn secondary btn-compact">Locations</a>
    </div>
  </header>

  <p class="toast" *ngIf="toast()">{{ toast() }}</p>
  <p class="error" *ngIf="error()">{{ error() }}</p>

  <p class="ws-loading" *ngIf="loading()">Loading locations…</p>

  <div class="gis-layout" [class.gis-layout--loading]="loading()">
    <aside class="page-card gis-sidebar">
      <label class="gis-field">
        <span>1. Sector</span>
        <select [ngModel]="sectorId()" (ngModelChange)="onSectorChange($event)">
          <option [ngValue]="null">Select sector…</option>
          <option *ngFor="let s of sectors()" [ngValue]="s.id">{{ s.name }} ({{ s.code }})</option>
        </select>
      </label>

      <label class="gis-field" *ngIf="sectorId() != null && sectorPlots().length">
        <span>2. Plot</span>
        <select [ngModel]="selectedPlotId()" (ngModelChange)="onPlotSelect($event)">
          <option [ngValue]="null">Select plot…</option>
          <option *ngFor="let row of sectorPlots()" [ngValue]="row.location.id">
            {{ plotOptionLabel(row) }}
          </option>
        </select>
      </label>

      <div class="gis-stats" *ngIf="sectorId() != null">
        <span>Ready for LiDAR</span>
        <strong [class.gis-stats-warn]="!boundaryStats().ready">
          {{ boundaryStats().withBoundary }} / {{ boundaryStats().total }}
        </strong>
      </div>

      <div class="gis-bulk-panel" *ngIf="sectorId() != null && bulkPreview() as preview">
        <h4 class="gis-bulk-title">Bulk file ready</h4>
        <p class="gis-bulk-file" *ngIf="bulkFileName()">{{ bulkFileName() }}</p>
        <div class="gis-bulk-summary" *ngIf="bulkPreviewSummary() as summary">
          <span class="gis-bulk-pill gis-bulk-pill--ok">{{ summary.matched }} plots matched</span>
          <span class="gis-bulk-pill gis-bulk-pill--warn" *ngIf="summary.unmatchedFeatures">
            {{ summary.unmatchedFeatures }} not matched
          </span>
        </div>
        <div class="gis-bulk-actions">
          <button
            type="button"
            class="btn primary btn-compact"
            (click)="applyBulkImport()"
            [disabled]="bulkImporting() || !preview.matches.length"
          >
            {{ bulkImporting() ? 'Saving…' : 'Save all matched' }}
          </button>
          <button type="button" class="btn secondary btn-compact" (click)="clearBulkPreview()" [disabled]="bulkImporting()">
            Cancel
          </button>
        </div>
      </div>

      <p class="ws-empty gis-empty" *ngIf="sectorId() != null && !sectorPlots().length">
        No plots in this sector. Add plots under Locations first.
      </p>
    </aside>

    <section class="page-card gis-map-panel" *ngIf="sectorId() != null; else pickSector">
      <ol class="gis-steps" aria-label="How to set a plot boundary">
        <li [class.gis-steps__item--done]="selectedPlotId() != null">Select plot</li>
        <li [class.gis-steps__item--done]="draftPoints().length >= 3 || drawing()">Import or draw shape</li>
        <li>Save</li>
      </ol>

      <div class="gis-map-toolbar">
        <div>
          <h3 class="panel-title">{{ selectedPlot()?.name || 'Select a plot' }}</h3>
          <p class="gis-status-line">{{ statusLine() }}</p>
        </div>
      </div>

      <div class="gis-actions-row" *ngIf="selectedPlot()">
        <label class="btn secondary btn-compact gis-file-btn">
          Import GeoJSON
          <input type="file" accept=".json,.geojson,application/json" (change)="onGeoJsonFileSelected($event)" hidden />
        </label>
        <button type="button" class="btn secondary btn-compact" (click)="editOnMap()" [disabled]="drawing()">
          {{ hasBoundary(selectedPlot()!) ? 'Edit on map' : 'Draw on map' }}
        </button>
        <ng-container *ngIf="drawing() || draftPoints().length">
          <button type="button" class="btn secondary btn-compact" (click)="undoPoint()" [disabled]="!draftPoints().length">
            Undo
          </button>
          <button
            type="button"
            class="btn secondary btn-compact"
            (click)="finishDrawing()"
            *ngIf="drawing()"
            [disabled]="draftPoints().length < 3"
          >
            Done
          </button>
          <button type="button" class="btn-link gis-reset-btn" (click)="clearDraft()">Reset</button>
        </ng-container>
      </div>

      <div class="gis-map-wrap">
        <div #mapHost class="gis-map" role="img" aria-label="Plot boundary map"></div>
        <p class="gis-map-hint" *ngIf="drawing()">Click each corner on the map, then press Done.</p>
      </div>

      <footer class="gis-save-row">
        <button
          type="button"
          class="btn primary"
          (click)="saveBoundary()"
          [disabled]="saving() || !selectedPlot() || draftPoints().length < 3"
        >
          {{ saving() ? 'Saving…' : '3. Save boundary' }}
        </button>
        <button
          type="button"
          class="btn secondary"
          (click)="removeBoundary()"
          [disabled]="saving() || !selectedPlot() || !hasBoundary(selectedPlot()!)"
        >
          Remove
        </button>
      </footer>
    </section>

    <ng-template #pickSector>
      <section class="page-card gis-map-panel gis-map-panel--empty">
        <p class="ws-empty">Select a sector to begin.</p>
      </section>
    </ng-template>
  </div>
</div>

<app-confirm-dialog
  [open]="confirmOpen()"
  [title]="confirmTitle()"
  [message]="confirmMessage()"
  [confirmLabel]="confirmLabel()"
  [destructive]="confirmDestructive()"
  cancelLabel="No"
  (confirm)="onConfirmDialogConfirm()"
  (cancel)="onConfirmDialogCancel()"
/>
